/**
 * Delivery date + delivery slot picker, saved as cart attributes.
 *
 * Markup lives in snippets/cart-delivery-options.liquid, rendered in the cart
 * drawer footer and in the cart page footer. Ported from the tree2me theme and
 * rewritten as a custom element so it survives this theme's section re-renders
 * (cart.js swaps the drawer's `.drawer__inner` and the footer's `.js-contents`
 * with fresh HTML on every quantity change).
 *
 * On /cart the drawer and the page footer are both on screen, so the chosen
 * date/slot and what the cart holds are kept once, at module level, and every
 * <delivery-options> element renders the same state. Saves go through a single
 * queue so two cart writes never overlap.
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('delivery-options')) return;

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  var WEEKDAYS = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  // One message, in two colours. Blocking checkout re-colours the standing
  // prompt rather than swapping the text, so the line never changes height.
  var INCOMPLETE_MESSAGE = 'Choose a delivery date and slot to continue.';
  var BLOCKED_MESSAGE = 'Please choose a delivery date and time slot before checking out.';
  var SAVED_EVENT = 'cart:delivery-options-saved';

  function updateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  /* ----------------------------------------------------------------------
     Date helpers
     ---------------------------------------------------------------------- */

  function parseISO(value) {
    var parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!parts) return null;
    var date = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
    return isNaN(date.getTime()) ? null : date;
  }

  function toISO(date) {
    var m = String(date.getMonth() + 1);
    var d = String(date.getDate());
    return date.getFullYear() + '-' + (m.length < 2 ? '0' + m : m) + '-' + (d.length < 2 ? '0' + d : d);
  }

  function longLabel(date) {
    return WEEKDAYS[date.getDay()] + ' ' + date.getDate() + ' ' + MONTHS[date.getMonth()] + ' ' + date.getFullYear();
  }

  /** Months since year 0, so two months can be compared as plain numbers. */
  function monthIndex(date) {
    return date.getFullYear() * 12 + date.getMonth();
  }

  /** 0 = Monday ... 6 = Sunday, to match the column order in the snippet. */
  function mondayFirstDay(date) {
    return (date.getDay() + 6) % 7;
  }

  /* ----------------------------------------------------------------------
     Shared state
     ---------------------------------------------------------------------- */

  var instances = [];

  /** The shopper's choice. Null until the first element connects. */
  var selection = null; // { dateIso, dateLabel, slotId, slotLabel }

  /** Whether the pickers are mandatory (from the snippet). */
  var required = false;

  /** Paid-slot configuration and what the cart currently holds. */
  var cartFacts = {
    feeProduct: 0,
    feeVariants: [],
    slotVariants: {}, // slot id -> variant id ('0' = free)
    feeInCart: '',
    otherItems: 0,
    // Accessories-only delivery: charged when the cart holds stands or lights but no tree.
    accessoryProduct: 0,
    accessoryVariant: '',
    accessoryInCart: '',
    treeType: '',
    accessoryTypes: [],
    hasTree: false,
    hasAccessory: false
  };

  /** One cart write at a time - see requestSave(). */
  var queue = { inFlight: false, body: '', pending: false };

  /** Fee lines as last painted by Liquid, so the footer is redrawn whenever they differ. */
  var renderedFeeState = '';

  function isComplete() {
    return Boolean(selection && selection.dateIso && selection.slotId);
  }

  /** Which fee variant the current selection should put in the cart, if any. */
  function wantedVariant() {
    // Never leave a cart holding nothing but a delivery fee.
    if (!cartFacts.otherItems || !selection || !selection.slotId) return '';
    var variant = cartFacts.slotVariants[selection.slotId];
    return variant && variant !== '0' ? variant : '';
  }

  /** The accessories-only delivery line the cart should hold: stands or lights present, no tree. */
  function wantedAccessory() {
    if (!cartFacts.accessoryVariant || cartFacts.hasTree || !cartFacts.hasAccessory) return '';
    return cartFacts.accessoryVariant;
  }

  /** Both fee lines in one string, so a change to either is noticed. */
  function feeState() {
    return cartFacts.feeInCart + '|' + cartFacts.accessoryInCart;
  }

  function readCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    var feeInCart = '';
    var otherItems = 0;
    var accessoryInCart = '';
    var hasTree = false;
    var hasAccessory = false;

    cart.items.forEach(function (line) {
      if (cartFacts.feeProduct && line.product_id === cartFacts.feeProduct) {
        feeInCart = String(line.variant_id);
      } else if (cartFacts.accessoryProduct && line.product_id === cartFacts.accessoryProduct) {
        accessoryInCart = String(line.variant_id);
      } else {
        otherItems += line.quantity;
        if (line.product_type === cartFacts.treeType) hasTree = true;
        if (cartFacts.accessoryTypes.indexOf(line.product_type) !== -1) hasAccessory = true;
      }
    });

    cartFacts.feeInCart = feeInCart;
    cartFacts.otherItems = otherItems;
    cartFacts.accessoryInCart = accessoryInCart;
    cartFacts.hasTree = hasTree;
    cartFacts.hasAccessory = hasAccessory;
    return true;
  }

  function syncAll() {
    instances.forEach(function (instance) {
      instance.render();
    });
  }

  /* ----------------------------------------------------------------------
     Redrawing the cart through the theme's own section rendering
     ---------------------------------------------------------------------- */

  /** Same shape as CartItems.getSectionsToRender() in cart.js. */
  function sectionsToRender() {
    var list = [];
    if (document.getElementById('CartDrawer')) {
      list.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }
    var items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) {
      list.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    }
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) {
      list.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      list.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    return list;
  }

  /**
   * Apply the `sections` returned by cart/update.js, the way cart.js does after
   * a quantity change. Only called when the fee line actually moved - a plain
   * date change doesn't need to touch the line items.
   */
  function renderSections(cart) {
    if (!cart || !cart.sections) return;
    var empty = cart.item_count === 0;

    sectionsToRender().forEach(function (entry) {
      var html = cart.sections[entry.section];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');

      if (entry.id === 'CartDrawer' && !empty) {
        // Swap only the line items and the totals so this picker (and whichever
        // panel the shopper has open) stays put. Mirrors CartItems.onCartUpdate.
        ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (selector) {
          var target = document.querySelector('#CartDrawer ' + selector);
          var source = doc.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });

        // The accessories-only delivery note sits inside the picker, which is kept
        // in place above, so move just that box across.
        var feeTarget = document.querySelector('#CartDrawer .cdo__accessory-fee');
        var feeSource = doc.querySelector('.cdo__accessory-fee');
        var feeAnchor = document.querySelector('#CartDrawer delivery-options [data-cdo-status]');
        if (feeTarget && !feeSource) {
          feeTarget.remove();
        } else if (feeTarget && feeSource) {
          feeTarget.replaceWith(feeSource);
        } else if (feeSource && feeAnchor) {
          feeAnchor.insertAdjacentElement('afterend', feeSource);
        }
        return;
      }

      var host = document.getElementById(entry.id);
      if (!host) return;
      var target = host.querySelector(entry.selector) || host;
      var source = doc.querySelector(entry.selector);
      if (source) target.innerHTML = source.innerHTML;
    });

    [
      document.querySelector('cart-drawer'),
      document.querySelector('cart-drawer-items'),
      document.querySelector('cart-items'),
      document.getElementById('main-cart-footer')
    ].forEach(function (node) {
      if (node) node.classList.toggle('is-empty', empty);
    });
  }

  /* ----------------------------------------------------------------------
     Saving
     ---------------------------------------------------------------------- */

  function buildBody() {
    var body = {
      attributes: {
        'Delivery Date': selection.dateLabel,
        _delivery_date: selection.dateIso,
        'Delivery Slot': selection.slotLabel,
        _delivery_slot: selection.slotId
      }
    };

    // One request for both: `updates` sets the chosen fee variant to 1 and every
    // other one to 0, so swapping between paid slots is a single round trip.
    if (cartFacts.feeVariants.length || cartFacts.accessoryVariant) {
      var wanted = wantedVariant();
      body.updates = {};
      cartFacts.feeVariants.forEach(function (variant) {
        body.updates[variant] = variant === wanted ? 1 : 0;
      });
      if (cartFacts.accessoryVariant) {
        body.updates[cartFacts.accessoryVariant] = wantedAccessory() ? 1 : 0;
      }

      var sections = sectionsToRender();
      if (sections.length) {
        body.sections = sections.map(function (entry) {
          return entry.section;
        });
        body.sections_url = window.location.pathname;
      }
    }

    return body;
  }

  /**
   * Persist to the cart straight away so accelerated checkout buttons - which
   * never submit the cart form - still carry the selection.
   *
   * Never overlap two cart writes. Aborting the in-flight fetch wouldn't help:
   * the POST has already reached Shopify, so a cancelled earlier request can
   * still land after a later one and win. Instead, hold one request at a time
   * and re-send afterwards if anything changed meanwhile - each request carries
   * the full desired state, so the last one to run is correct.
   */
  function requestSave(from) {
    if (!selection) return;
    var body = JSON.stringify(buildBody());

    if (queue.inFlight) {
      // The very same state is already on its way: nothing more to do. Anything
      // else is picked up by one more request once this one lands - it is
      // rebuilt from the latest state at that point, not from this snapshot.
      queue.pending = body !== queue.body;
      return;
    }

    send(from, body);
  }

  function send(from, body) {
    var feeWas = feeState();
    var result = null;

    queue.inFlight = true;
    queue.body = body;
    queue.pending = false;
    if (from) from.classList.add('is-saving');

    fetch(updateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/update.js responded ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        readCart(cart);
        result = cart;
      })
      .catch(function (error) {
        // The hidden inputs still ride along with the form POST, so checkout is safe.
        console.warn('Failed to save delivery options:', error);
      })
      .then(function () {
        queue.inFlight = false;
        queue.body = '';
        if (from) from.classList.remove('is-saving');

        if (queue.pending) {
          requestSave(from);
          return;
        }

        syncAll();
        if (!result) return;

        if (feeState() !== renderedFeeState) {
          renderedFeeState = feeState();
          renderSections(result);
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'delivery-options', cartData: result });
          }
        }

        document.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: result }));
      });
  }

  /* ----------------------------------------------------------------------
     Checkout lock
     ----------------------------------------------------------------------
     Hold checkout until both fields are set. Two separate gates are needed:
     the Checkout button submits the cart form, the wallet buttons do not
     (they are third-party widgets), so those get a transparent shield on top.

     Listeners sit on the document, in the capture phase, because cart.js
     replaces the drawer form and its contents wholesale after every change.
     ---------------------------------------------------------------------- */

  function isCartForm(form) {
    return Boolean(form && form.tagName === 'FORM' && form.classList.contains('cart__contents'));
  }

  function instanceForForm(form) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].dataset.formId === form.id) return instances[i];
    }
    return instances[0] || null;
  }

  function guardCheckout() {
    document.addEventListener('click', function (event) {
      if (!required || isComplete()) return;
      var trigger = event.target.closest && event.target.closest('[name="checkout"]');
      if (!trigger) return;
      var form = trigger.form || trigger.closest('form');
      if (!isCartForm(form)) return;

      event.preventDefault();
      event.stopPropagation();
      var instance = instanceForForm(form);
      if (instance) instance.reject();
    }, true);

    document.addEventListener('submit', function (event) {
      if (!required || isComplete()) return;
      if (!isCartForm(event.target)) return;
      // Only the Checkout button is held back. A plain cart update (e.g. Enter
      // in a quantity field with JavaScript disabled) is fine.
      if (event.submitter && event.submitter.name !== 'checkout') return;

      event.preventDefault();
      event.stopPropagation();
      var instance = instanceForForm(event.target);
      if (instance) instance.reject();
    }, true);
  }

  /* ----------------------------------------------------------------------
     The element
     ---------------------------------------------------------------------- */

  class DeliveryOptions extends HTMLElement {
    connectedCallback() {
      this.minDate = parseISO(this.dataset.minDate);
      this.maxDate = parseISO(this.dataset.maxDate);
      if (!this.minDate || !this.maxDate) return;

      if (instances.indexOf(this) === -1) instances.push(this);

      this.adoptConfig();
      if (!this.built) this.build();
      this.listen();
      this.render();

      // The cart and the saved slot can disagree - a slot chosen in another tab,
      // a line removed from the cart page. Put them back in step.
      if ((cartFacts.feeVariants.length && wantedVariant() !== cartFacts.feeInCart) || wantedAccessory() !== cartFacts.accessoryInCart) {
      requestSave(this);
      }
    }

    disconnectedCallback() {
      var index = instances.indexOf(this);
      if (index !== -1) instances.splice(index, 1);
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    }

    /**
     * A freshly rendered element carries the server's latest picture of the
     * cart in its data attributes, so it refreshes the shared facts. The
     * shopper's own selection is only taken from the markup when nothing is
     * mid-save - otherwise what they just picked wins over what Liquid saw.
     */
    adoptConfig() {
      var self = this;
      var data = this.dataset;

      if (data.required === 'true') required = true;

      cartFacts.feeProduct = Number(data.feeProduct) || 0;
      cartFacts.feeVariants = (data.feeVariants || '').split(',').filter(Boolean);
      cartFacts.feeInCart = data.feeInCart || '';
      cartFacts.otherItems = Number(data.otherItems) || 0;
      cartFacts.accessoryProduct = Number(data.accessoryProduct) || 0;
      cartFacts.accessoryVariant = data.accessoryVariant || '';
      cartFacts.accessoryInCart = data.accessoryInCart || '';
      cartFacts.treeType = data.treeType || '';
      cartFacts.accessoryTypes = (data.accessoryTypes || '').split('|').filter(Boolean);
      cartFacts.hasTree = data.hasTree === 'true';
      cartFacts.hasAccessory = data.hasAccessory === 'true';
      renderedFeeState = feeState();

      this.querySelectorAll('.cdo__slot-input').forEach(function (input) {
        cartFacts.slotVariants[input.value] = input.dataset.slotVariant || '0';
      });

      if (!selection || (!queue.inFlight && !queue.pending)) {
        var iso = data.selectedDate || '';
        var date = parseISO(iso);
        if (!date || self.outOfRange(date)) iso = '';
        selection = {
          dateIso: iso,
          dateLabel: iso ? (data.selectedDateLabel || longLabel(date)) : '',
          slotId: data.selectedSlot || '',
          slotLabel: data.selectedSlotLabel || ''
        };
        if (selection.slotId && !(selection.slotId in cartFacts.slotVariants)) {
          selection.slotId = '';
          selection.slotLabel = '';
        }
      }
    }

    build() {
      this.built = true;
      var self = this;

      this.grid = this.querySelector('[data-cdo-grid]');
      this.monthLabel = this.querySelector('[data-cdo-month]');
      this.prevBtn = this.querySelector('[data-cdo-prev]');
      this.nextBtn = this.querySelector('[data-cdo-next]');
      this.status = this.querySelector('[data-cdo-status]');
      this.inputs = {
        dateLabel: this.querySelector('[data-cdo-input="date-label"]'),
        dateIso: this.querySelector('[data-cdo-input="date-iso"]'),
        slotLabel: this.querySelector('[data-cdo-input="slot-label"]'),
        slotId: this.querySelector('[data-cdo-input="slot-id"]')
      };

      // The footer this picker sits in: the lock class goes here, because the
      // Checkout button lives beside the picker rather than inside a form.
      this.scope = this.closest('.drawer__footer, .cart__footer') || this.parentElement || this;

      var start = parseISO(selection.dateIso) || this.minDate;
      this.viewMonth = new Date(start.getFullYear(), start.getMonth(), 1);

      this.addEventListener('click', function (event) {
        var toggle = event.target.closest('[data-cdo-toggle]');
        if (toggle && self.contains(toggle)) {
          event.preventDefault();
          self.togglePanel(toggle.dataset.cdoToggle);
          return;
        }

        var nav = event.target.closest('[data-cdo-prev], [data-cdo-next]');
        if (nav && self.contains(nav)) {
          event.preventDefault();
          self.shiftMonth(nav.hasAttribute('data-cdo-prev') ? -1 : 1);
          return;
        }

        var day = event.target.closest('[data-cdo-day]');
        if (day && self.contains(day) && !day.disabled) {
          event.preventDefault();
          self.selectDate(parseISO(day.dataset.cdoDay));
        }
      });

      this.addEventListener('change', function (event) {
        if (event.target.classList.contains('cdo__slot-input')) self.selectSlot(event.target);
      });

      this.initShield();
    }

    /** Wallet buttons never reach a submit handler, so they get a shield on top. */
    initShield() {
      if (!required) return;
      var wallets = this.scope.querySelector('.additional-checkout-buttons');
      if (!wallets || wallets.querySelector('.cdo__shield')) return;
      var self = this;

      var shield = document.createElement('button');
      shield.type = 'button';
      shield.className = 'cdo__shield';
      shield.setAttribute('aria-label', BLOCKED_MESSAGE);
      shield.addEventListener('click', function (event) {
        event.preventDefault();
        self.reject();
      });
      wallets.appendChild(shield);
    }

    /**
     * The theme publishes this whenever a line changes. Emptying the cart of
     * products has to take the delivery fee with it. Events without a full
     * cart (e.g. product-form's add.js response) are ignored: the theme
     * re-renders the drawer in that case and a new element picks it up.
     */
    listen() {
      if (this.unsubscribe || typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS) return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (!event || event.source === 'delivery-options') return;
        if (!cartFacts.feeVariants.length && !cartFacts.accessoryVariant) return;
        if (!readCart(event.cartData)) return;
        if (wantedVariant() !== cartFacts.feeInCart || wantedAccessory() !== cartFacts.accessoryInCart) requestSave(null);
      });
    }

    outOfRange(date) {
      return date < this.minDate || date > this.maxDate;
    }

    /* --- rendering --------------------------------------------------------- */

    /** Paint the shared selection into this element. */
    render() {
      if (!this.built) return;

      var date = parseISO(selection.dateIso);
      this.selectedDate = date && !this.outOfRange(date) ? date : null;
      this.renderCalendar();

      this.inputs.dateIso.value = selection.dateIso;
      this.inputs.dateLabel.value = selection.dateLabel;
      this.inputs.slotId.value = selection.slotId;
      this.inputs.slotLabel.value = selection.slotLabel;

      this.setValueText('date', selection.dateLabel, 'Choose a date');
      var slotInput = this.querySelector('.cdo__slot-input[value="' + selection.slotId + '"]'); var slotFee = slotInput && slotInput.dataset.slotFee ? ' ' + slotInput.dataset.slotFee : ''; this.setValueText('slot', selection.slotLabel ? selection.slotLabel + slotFee : '', 'Choose a time');

      this.querySelectorAll('.cdo__slot-input').forEach(function (input) {
        input.checked = input.value === selection.slotId;
      });

      if (isComplete()) this.rejected = false;
      this.updateLock();
      this.refreshStatus();
    }

    renderCalendar() {
      var view = this.viewMonth;
      var daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
      var lead = mondayFirstDay(view);
      var selectedISO = this.selectedDate ? toISO(this.selectedDate) : '';
      var html = '';

      for (var blank = 0; blank < lead; blank++) {
        html += '<span class="cdo__day cdo__day--blank" aria-hidden="true"></span>';
      }

      for (var day = 1; day <= daysInMonth; day++) {
        var date = new Date(view.getFullYear(), view.getMonth(), day);
        var iso = toISO(date);
        var disabled = this.outOfRange(date);
        var selected = iso === selectedISO;

        html += '<button type="button" class="cdo__day' + (selected ? ' is-selected' : '') + '"' +
          ' data-cdo-day="' + iso + '"' +
          (disabled ? ' disabled aria-disabled="true"' : '') +
          ' aria-pressed="' + (selected ? 'true' : 'false') + '"' +
          ' aria-label="' + longLabel(date) + '">' + day + '</button>';
      }

      this.grid.innerHTML = html;
      this.monthLabel.textContent = MONTHS[view.getMonth()] + ' ' + view.getFullYear();
      this.prevBtn.disabled = monthIndex(view) <= monthIndex(this.minDate);
      this.nextBtn.disabled = monthIndex(view) >= monthIndex(this.maxDate);
    }

    setValueText(name, text, placeholder) {
      var node = this.querySelector('[data-cdo-value="' + name + '"]');
      if (!node) return;
      node.textContent = text || placeholder;
      node.classList.toggle('is-empty', !text);
    }

    setStatus(message, type) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.className = 'cdo__status' + (type ? ' cdo__status--' + type : '');
    }

    /**
     * The only message there is: a standing prompt while something is missing.
     * There is deliberately no "saving" or "saved" state - the chosen date and
     * slot appear in the rows above the moment they are picked, and a status
     * line appearing and disappearing shifts everything below it.
     */
    refreshStatus() {
      if (required && !isComplete()) {
        this.setStatus(INCOMPLETE_MESSAGE, this.rejected ? 'error' : 'hint');
      } else {
        this.setStatus('');
      }
    }

    updateLock() {
      if (!required || !this.scope) return;
      this.scope.classList.toggle('cdo-locked', !isComplete());
    }

    reject() {
      this.rejected = true;
      this.refreshStatus();
      this.openPanel(selection.dateIso ? 'slot' : 'date');
      this.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    /* --- panels ------------------------------------------------------------ */

    openPanel(name) {
      var self = this;
      ['date', 'slot'].forEach(function (key) {
        var panel = self.querySelector('[data-cdo-panel="' + key + '"]');
        var toggle = self.querySelector('[data-cdo-toggle="' + key + '"]');
        var open = key === name;
        if (panel) panel.classList.toggle('is-open', open);
        if (toggle) toggle.setAttribute('aria-expanded', String(open));
      });
    }

    togglePanel(name) {
      var panel = this.querySelector('[data-cdo-panel="' + name + '"]');
      this.openPanel(panel && panel.classList.contains('is-open') ? null : name);
    }

    shiftMonth(offset) {
      this.viewMonth = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + offset, 1);
      this.renderCalendar();
    }

    /* --- choices ----------------------------------------------------------- */

    selectDate(date) {
      if (!date || this.outOfRange(date)) return;
      selection.dateIso = toISO(date);
      selection.dateLabel = longLabel(date);
      syncAll();

      // Nudge the shopper straight into the slot picker the first time round.
      this.openPanel(selection.slotId ? null : 'slot');
      requestSave(this);
    }

    selectSlot(input) {
      selection.slotId = input.value;
      selection.slotLabel = input.dataset.slotLabel;
      syncAll();
      this.openPanel(null);
      requestSave(this);
    }
  }

  guardCheckout();
  customElements.define('delivery-options', DeliveryOptions);
})();
